import React, { useEffect } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import styled, { css } from 'styled-components';
import { Close } from '@zocdoc-frontend-common/assets-icons';
import Button from '@zocdoc-frontend-common/components-button';
import Checkbox from '@zocdoc-frontend-common/components-checkbox';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import Form from '@zocdoc-frontend-common/components-form';
import { Link } from '@zocdoc-frontend-common/components-link';
import { Spinner } from '@zocdoc-frontend-common/components-spinner';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import {
    blue100,
    red10,
    red70,
    slate30,
} from '@zocdoc-mezzanine/foundations-colors';
import PasswordRules from '../PasswordRules/PasswordRules';
import { elementNames, errorNames } from './constants';

const anchors = {
    terms: '/about/terms',
    privacy: '/about/privacypolicy',
};

const SignUpFormView = (props) => {
    const {
        errors,
        handleBlur,
        handleChange,
        handleClickLink,
        handleClickSubmit,
        handleFocus,
        highlightedFields,
        passwordStatusChecks,
        handleChangeCheckbox,
        loading,
        touched,
        values,
        showRememberMe,
        onClickLoginLink,
        submitted,
        inlineEmailLoading,
        signInFailed,
        emailAlreadyInUseError,
        sendMetric,
        /* used by Storybook to mock image */
        loadingImageSrc = '/images/zee-long-sync.gif',
    } = props;

    useEffect(() => {
        if (signInFailed) {
            sendMetric({
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                element: 'Error',
                component: 'Signin failed try again error',
                action: 'Saw sign in failed try again error',
            });
        }
    }, [sendMetric, signInFailed]);

    return (
        <SignUpForm onSubmit={handleClickSubmit} data-test="sign-up-form">
            {loading && (
                <LoadingImgContainer>
                    <LoadingOverlay />
                    <img
                        alt="Loading"
                        data-test="loading-image"
                        src={loadingImageSrc}
                    />
                </LoadingImgContainer>
            )}
            <Section>
                <Heading>To finish booking, complete your profile.</Heading>
                <Subheading>
                    We’ll have your profile handy for follow-ups and other
                    appointments.
                </Subheading>
            </Section>

            <Section>
                <SectionTitle id="email-title">Email</SectionTitle>
                <InputRow>
                    <StyledTextbox
                        aria-labelledby="email-title"
                        autoComplete="username"
                        className="exclude-from-playback"
                        data-test="email"
                        data-uem-id="email"
                        name={elementNames.email}
                        inputMode="email"
                        invalid={Boolean(highlightedFields[elementNames.email])}
                        value={values[elementNames.email]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        tabIndex="0"
                        type="email"
                    >
                        {inlineEmailLoading && !errors[errorNames.emailError] && (
                            <SpinnerWrapper>
                                <Spinner inline size={24} />
                            </SpinnerWrapper>
                        )}
                    </StyledTextbox>
                </InputRow>

                {errors[errorNames.emailError] && (
                    <ErrorMessage data-test="email-error-empty">
                        {values[elementNames.email]
                            ? 'Please type a valid email.'
                            : 'Please type an email.'}
                    </ErrorMessage>
                )}

                {!errors[errorNames.emailError] && emailAlreadyInUseError && (
                    <TakenHintContainer
                        submitted={submitted}
                        data-test="email-taken-container"
                    >
                        <CTA>
                            <Text data-test="email-taken-text">
                                Looks like you already have an account.
                            </Text>
                            <LoginLink
                                onClick={onClickLoginLink}
                                data-test="inline-email-login-link"
                            >
                                Log in now
                            </LoginLink>
                        </CTA>
                    </TakenHintContainer>
                )}
            </Section>

            <Section>
                <SectionTitle id="confirm-email-title">
                    Confirm email
                </SectionTitle>
                <InputRow>
                    <StyledTextbox
                        aria-labelledby="confirm-email-title"
                        autoComplete="username"
                        className="exclude-from-playback"
                        data-test="confirm-email"
                        data-uem-id="confirm-email"
                        name={elementNames.confirmEmail}
                        inputMode="email"
                        invalid={Boolean(
                            highlightedFields[elementNames.confirmEmail]
                        )}
                        value={values[elementNames.confirmEmail]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        tabIndex="0"
                        type="email"
                    />
                </InputRow>

                {errors[errorNames.confirmEmailError] && (
                    <ErrorMessage data-test="confirm-email-error">
                        The confirm email must match your email address.
                    </ErrorMessage>
                )}
            </Section>

            <Section>
                <SectionTitle>Password</SectionTitle>
                <InputRow>
                    <StyledTextbox
                        autoComplete="new-password"
                        className="exclude-from-playback"
                        data-test="create-password-textbox"
                        name={elementNames.password}
                        invalid={Boolean(
                            highlightedFields[elementNames.password]
                        )}
                        value={values[elementNames.password]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        type="password"
                        tabIndex="0"
                    />
                </InputRow>
                {touched[elementNames.password] && (
                    <PasswordRules
                        inBooking
                        data-test="password-rules"
                        statusChecks={passwordStatusChecks}
                        animateRuleListAppearance={true}
                    />
                )}

                {errors[errorNames.serverSideWeakPasswordError] && (
                    <WeakPasswordErrorWrapper
                        data-test={'password-too-weak-error'}
                    >
                        <Bullet>
                            <Close color={red70} />
                        </Bullet>
                        Password must not be easy to guess
                    </WeakPasswordErrorWrapper>
                )}
            </Section>

            <Section>
                <VisibilitySensor>
                    <FlushCheckbox
                        data-test="terms-and-privacy-checkbox"
                        data-uem-id="terms-and-privacy-checkbox"
                        iconStyle={{ marginLeft: '0' }}
                        inputProps={{ name: elementNames.termsOfService }}
                        onChange={handleChangeCheckbox}
                        invalidMessage={
                            errors[errorNames.termsOfServiceError]
                                ? 'Please read and accept the Terms of Use and Privacy Policy.'
                                : undefined
                        }
                        checked={values[elementNames.termsOfService]}
                    >
                        <CheckboxLabel>
                            {`I have read and accept Zocdoc's `}
                            <Link
                                data-test="terms"
                                href={anchors.terms}
                                target="_blank"
                                name={elementNames.termsOfServiceLink}
                                onClick={handleClickLink}
                            >
                                Terms of Use
                            </Link>
                            {' and '}
                            <Link
                                data-test="privacy"
                                href={anchors.privacy}
                                target="_blank"
                                name={elementNames.privacyPolicyLink}
                                onClick={handleClickLink}
                            >
                                Privacy Policy
                            </Link>
                            .
                        </CheckboxLabel>
                    </FlushCheckbox>
                </VisibilitySensor>

                {showRememberMe && (
                    <FlushCheckbox
                        checked={values[elementNames.rememberMe]}
                        data-test="remember-me-checkbox"
                        data-uem-id="remember-me-checkbox"
                        iconStyle={{ marginLeft: '0' }}
                        inputProps={{ name: elementNames.rememberMe }}
                        onChange={handleChangeCheckbox}
                    >
                        Keep me logged in
                    </FlushCheckbox>
                )}
            </Section>

            <ButtonContainer>
                <StyledButton
                    data-test="submit-button"
                    disabled={loading}
                    type="submit"
                >
                    Continue
                </StyledButton>
                {errors[errorNames.submitError] ? (
                    <ErrorMessage data-test="create-patient-error-message">
                        Account creation failed, try again later.
                    </ErrorMessage>
                ) : signInFailed ? (
                    <ErrorMessage data-test="sign-in-error-message">
                        Account creation failed, try again later.
                    </ErrorMessage>
                ) : null}
            </ButtonContainer>
        </SignUpForm>
    );
};

const SignUpForm = styled(Form)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: auto;
    /* Set spacing between adjacent divs that are direct children */
    > div + div {
        margin-top: 24px;
    }

    /* Override FEC components to use Mezzanine colors */
    ${Link} {
        border-bottom: 1px solid ${blue100};
        color: ${blue100};
        font-family: ${FontFamily.SharpSansBold};
        text-decoration: none;
    }

    ${ErrorMessage} {
        color: ${red70};
    }
`;

const InputRow = styled.div`
    display: flex;
    width: 100%;
    > div {
        flex-basis: 100%;
    }
    > div:not(:last-of-type) > input {
        border-right-width: 0px;
    }
`;

const SectionTitle = styled.label`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    margin-bottom: 4px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Heading = styled.h1`
    font: 28px/35px ${FontFamily.SharpSansBold};
    ${breakpoint.small} {
        font-size: 32px;
    }
    margin-bottom: 12px;
`;

const Subheading = styled.h2`
    font: 15px/24px ${FontFamily.SharpSansMedium};
    ${breakpoint.small} {
        font-size: 16px;
    }
    margin-bottom: 4px;
`;

const StyledTextbox = styled(Textbox)`
    border-color: ${slate30};

    min-width: 0;
    ${(p) =>
        p.invalid &&
        css`
            border-color: ${red70};
            border-right-width: 1px !important;
        `};
`;

const ButtonContainer = styled.div`
    margin-top: 24px;
    margin-bottom: 40px;

    ${breakpoint.small} {
        margin-bottom: 72px;
    }
    width: 100%;

    /* space between button and error text*/
    > * + * {
        margin-top: 3px;
    }
`;

const StyledButton = styled(Button)`
    box-sizing: border-box;
    font: 16px/28px ${FontFamily.SharpSansSemibold};
    ${breakpoint.small} {
        font-size: 18px;
    }
    width: 100%;
    padding: 9px 15px 9px 15px;
`;

const FlushCheckbox = styled(Checkbox)`
    padding-left: 0;
    padding-top: 20px;
    ${breakpoint.small} {
        padding-top: 16px;
    }
`;

const CheckboxLabel = styled.div`
    color: ${blue100};
    font: 16px/1.333 ${FontFamily.SharpSansMedium};
    padding-top: 1px;
    padding-bottom: 3px;
`;

const TakenHintContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    border: 1px solid;
    border-color: ${(props) => (props.submitted ? red70 : slate30)};
    margin-top: 8px;
    padding: 10px 0px 10px 16px;
    font: 15px ${FontFamily.SharpSansMedium};
    line-height: 24px;
    background-color: ${(props) => (props.submitted ? red10 : 'inherent')};
    ${breakpoint.medium} {
        height: 48px;
    }
`;

const CTA = styled.span`
    align-self: center;
`;

const Text = styled.span`
    padding-right: 4px;
`;

const LoginLink = styled.a`
    display: inline-block;
    border-bottom: 2px dotted;
    color: rgb(43, 191, 237);
    flex-basis: 100px;
    cursor: pointer;
`;

const SpinnerWrapper = styled.div`
    position: absolute;
    height: 24px;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    > div > div {
        :before {
            border: 3px solid ${slate30};
            border-top-color: ${blue100};
        }
    }
`;

const Bullet = styled.div`
    height: 10px;
    width: 10px;
    display: inline-block;
    align-items: center;
    justify-content: center;
    margin-right: 5px;
    margin-left: 2px;
    font-size: 10px;
    padding-bottom: 10px;
`;

const WeakPasswordErrorWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
    color: ${red70};
    font: 13px/24px ${FontFamily.SharpSansMedium};
    min-width: 276px;
    z-index: 0;
    overflow: hidden;
`;

const LoadingImgContainer = styled.div`
    height: 100%;
    width: 100%;
    position: fixed;
    left: 0;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    img {
        z-index: 11;
    }
`;

const LoadingOverlay = styled.div`
    background-color: #000;
    opacity: 0.6;
    height: 100%;
    width: 100%;
    position: fixed;
    left: 0;
    top: 0;
`;

export default SignUpFormView;
